import React, {useCallback, useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

const separators = [',', '.', ' '];
const DIGITS_PER_SET = 10;
const REPETITIONS = 3;
const RESET_THRESHOLD = DIGITS_PER_SET * (REPETITIONS - 1);
const DEFAULT_TIMING_FUNCTION = 'cubic-bezier(0.47, 1.64, 0.41, 0.8)';

const isNumeric = (num: string | number) =>
  (typeof num === 'number' || (typeof num === 'string' && num.trim() !== '')) &&
  !isNaN(num as number);

const getWidth = (characters: string[], numbersWidth?: number) => {
  let width = 1; // an extra 1px for not being cropped
  for (const character of characters) {
    if (separators.includes(character)) {
      width += 5;
    } else if (isNumeric(character)) {
      width += numbersWidth ?? 8;
    } else {
      width += 10;
    }
  }
  return width;
};

/**
 * Builds a per-digit cubic-bezier where digits further to the right
 * receive a more negative y1 value, creating a bigger initial downward
 * dip before rolling up.
 */
const getCascadeTimingFunction = (
  positionFromRight: number,
  totalDigits: number,
  intensity: number,
) => {
  const overshoot = (totalDigits - positionFromRight) * intensity;
  return `cubic-bezier(0.47, ${-overshoot}, 0.41, 0.8)`;
};

interface RollingNumbersProps {
  value: string;
  numbersWidth?: number;
  customTimingFunction?: string;
  /** When true, digits always animate by rolling upward (e.g. 9 → 0 scrolls up instead of down). */
  alwaysRollUp?: boolean;
  /** When true, digits further to the right dip down more before rolling up. */
  cascadeOvershoot?: boolean;
  /** Multiplier for the cascade overshoot effect (default 0.3). */
  cascadeIntensity?: number;
}

export const RollingNumbers = ({
  value,
  numbersWidth,
  customTimingFunction,
  alwaysRollUp,
  cascadeOvershoot,
  cascadeIntensity = 0.3,
}: RollingNumbersProps) => {
  const characters = value.toString().split('');

  const numericPositions = characters.reduce<number[]>((acc, char, i) => {
    if (isNumeric(char)) acc.push(i);
    return acc;
  }, []);

  return (
    <Container
      style={{
        width: getWidth(characters, numbersWidth),
      }}
    >
      {characters.map((character, i) => {
        /* Key by position from the right so digits stay aligned
           (ones, tens, hundreds…) when the string grows/shrinks.
           e.g. "987" → "1,234": the rightmost '7' and '4' share
           the same key, allowing the transition to fire. */
        const keyFromRight = characters.length - 1 - i;

        if (isNumeric(character)) {
          const digitIndex = numericPositions.indexOf(i);
          const positionFromRight = numericPositions.length - 1 - digitIndex;

          const timingFunction = cascadeOvershoot
            ? getCascadeTimingFunction(
                positionFromRight,
                numericPositions.length,
                cascadeIntensity,
              )
            : customTimingFunction;

          if (alwaysRollUp) {
            return (
              <RollingDigit
                key={keyFromRight}
                digit={parseInt(character)}
                numbersWidth={numbersWidth}
                timingFunction={timingFunction}
              />
            );
          }

          return (
            <Character
              style={{
                transform: `translateY(-${parseInt(character) * 100}%)`,
              }}
              key={keyFromRight}
              $numbersWidth={numbersWidth}
              $customTimingFunction={timingFunction}
            >
              {Array.from(Array(DIGITS_PER_SET)).map((_, j) => (
                <span key={j}>{j}</span>
              ))}
            </Character>
          );
        }

        return (
          <Character
            key={keyFromRight}
            $large={!separators.includes(character)}
            $small={separators.includes(character)}
          >
            {character}
          </Character>
        );
      })}
    </Container>
  );
};

/**
 * Manages a cumulative position so the digit always scrolls upward.
 *
 * Digits 0-9 are rendered {@link REPETITIONS} times. When the cumulative
 * position reaches {@link RESET_THRESHOLD}, the transition is momentarily
 * disabled and the position resets by one full cycle to prevent overflow.
 */
const RollingDigit = ({
  digit,
  numbersWidth,
  timingFunction,
}: {
  digit: number;
  numbersWidth?: number;
  timingFunction?: string;
}) => {
  const positionRef = useRef(digit);
  const prevDigitRef = useRef(digit);
  const isInitialRef = useRef(true);
  const [position, setPosition] = useState(digit);
  const [transitionEnabled, setTransitionEnabled] = useState(true);

  /* The cumulative position must be derived from the previous digit
     on each prop change. A ref alone cannot trigger a re-render with
     the updated translateY, so setState is intentional here. */
  useEffect(() => {
    if (isInitialRef.current) {
      isInitialRef.current = false;
      return;
    }

    const prev = prevDigitRef.current;
    if (digit !== prev) {
      /* Always-positive delta so the animation direction is always "up" */
      const delta = (digit - prev + DIGITS_PER_SET) % DIGITS_PER_SET;
      positionRef.current += delta;
      prevDigitRef.current = digit;
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- cumulative position requires re-render to update translateY
      setPosition(positionRef.current);
    }
  }, [digit]);

  const handleTransitionEnd = useCallback(() => {
    if (positionRef.current >= RESET_THRESHOLD) {
      setTransitionEnabled(false);
      const resetPos = positionRef.current % DIGITS_PER_SET;
      positionRef.current = resetPos;
      setPosition(resetPos);
      /* Double rAF ensures the non-animated reset paints before
         re-enabling transitions */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
        });
      });
    }
  }, []);

  return (
    <Character
      style={{transform: `translateY(-${position * 100}%)`}}
      onTransitionEnd={handleTransitionEnd}
      $numbersWidth={numbersWidth}
      $customTimingFunction={timingFunction}
      $disableTransition={!transitionEnabled}
    >
      {Array.from(Array(DIGITS_PER_SET * REPETITIONS)).map((_, i) => (
        <span key={i}>{i % DIGITS_PER_SET}</span>
      ))}
    </Character>
  );
};

const Container = styled.span`
  position: relative;
  display: inline-flex;
  overflow: hidden;
  height: 20px;
  /* Disabling rule below since this is a isolated component where
  animating the width will not affect the page layout */
  /* eslint-disable-next-line local-rules/css-layout-animation */
  transition: width 1s ease;
`;

const Character = styled.span<{
  $small?: boolean;
  $large?: boolean;
  $numbersWidth?: number;
  $customTimingFunction?: string;
  $disableTransition?: boolean;
}>`
  display: inline-flex;
  text-align: center;
  flex-direction: column;
  right: 0;
  line-height: 20px;
  transition: ${({$disableTransition, $customTimingFunction}) =>
    $disableTransition
      ? 'none'
      : `transform 1s
    ${$customTimingFunction ?? DEFAULT_TIMING_FUNCTION}`};
  position: static;
  flex-basis: ${({$numbersWidth}) => $numbersWidth ?? 8}px;
  width: ${({$numbersWidth}) => $numbersWidth ?? 8}px;
  opacity: 1;
  flex-shrink: 1;

  ${({$small}) =>
    $small &&
    css`
      width: 5px;
      flex-basis: 5px;
    `}

  ${({$large}) =>
    $large &&
    css`
      width: 10px;
      flex-basis: 10px;
    `}
`;
